// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import VueRouter from 'vue-router'
import VueHead from 'vue-head'
import VueMoment from 'vue-moment'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, email, min, max, numeric, confirmed, regex } from 'vee-validate/dist/rules'
import Axios from 'axios'
import Notifications from 'vue-notification'
import routes from './router'
import helpers from './utils/helpers'
import i18n from './i18n'
import { store } from './store'
import { capitalizeFilter } from './utils/filters/capitalize'
import { formatAddressFilter } from './utils/filters/formatAddress'
import { formatDateFilter } from './utils/filters/formatDate'
import { formatInsuranceFilter } from './utils/filters/formatInsurance'
import Toast from '@/mixins/Toast'
import App from '@/App'
import Config from '@/config'

// Define head
Vue.use(VueHead, {
	separator: '|',
	complement: 'Ius Populi',
})

// Define router
Vue.use(VueRouter)
const router = new VueRouter({
	routes,
	mode: 'history',
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) {
			/**
			 * When we simply scroll down on a page, pressing the back button will return us to the top of the page.
			 * This is because our 'previous position'’' before scrolling, was the top of the page.
			 * This conditional checks for this saved position stored in the savedPosition parameter.
			 */
			return savedPosition
		}
		if (to.hash) {
			/**
			 * When the next page is visited, it will have a hash in it's route. The conditional check will notice this hash.
			 * It returns an object. The selector value indicates the id of the content we want to jump to.
			 */
			return { selector: to.hash }
		}

		/**
		 * This returns an object with x and y coordinates for the top of the page. This is necessary for when
		 * we have just visited a page and there are no hash fragments. When there is nothing to scroll to and
		 * we also haven't manually scrolled down, we need the top of the page to be displayed.
		 */
		return { x: 0, y: 0 }
	},
})
router.beforeEach((to, from, next) => {
	const isOnline = !navigator.onLine ? false : true

	if (isOnline) {
		// Add router guards
		if (to.matched.some((record) => record.meta.requiresAuth)) {
			// This route requires to be authenticated, check if logged in
			// (if not, redirect to login with redirect_to param if available)
			if (!store.getters.loggedIn) {
				next({ name: 'login', query: { redirect_to: to.path } })
			} else {
				// Check for account type
				if (to.matched.some((record) => record.meta.requiresLawyer) && store.getters.isUser) {
					next({ name: 'error', params: { code: Config.ERROR_FORBIDDEN_ACCESS_TYPE, info: 'RequiresLawyer' } })
				} else if (to.matched.some((record) => record.meta.requiresUser) && !store.getters.isUser) {
					next({ name: 'error', params: { code: Config.ERROR_FORBIDDEN_ACCESS_TYPE, info: 'RequiresUser' } })
				}
				next()
			}
		} else if (to.matched.some((record) => record.meta.requiresVisitor)) {
			// This route requires to be a visitor, check if logged in (if true, redirect to dashboard)
			if (store.getters.loggedIn) {
				next({ name: 'dashboard' })
			} else {
				next()
			}
		} else {
			// All guards passed, go next
			next()
		}
	} else {
		// Setup offline mode
		store.dispatch('setupOfflineMode', to.fullPath)
	}
})

// Define moment
Vue.use(VueMoment)

// Define toast
Vue.use(Notifications)

// Define global mixins
Vue.mixin(Toast)

// Define global helpers
const AppHelpers = {
	install() {
		Vue.helpers = helpers
		Vue.prototype.$helpers = helpers
	},
}
Vue.use(AppHelpers)

// Define global filters
Vue.filter('capitalize', capitalizeFilter)
Vue.filter('formatAddress', formatAddressFilter)
Vue.filter('formatDate', formatDateFilter)
Vue.filter('formatInsurance', formatInsuranceFilter)

// Define config
Vue.config.productionTip = false

// Define validator provider components, rules and messages
Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)
extend('required', {
	...required,
	message: 'Campo è obbligatorio',
})
extend('email', {
	...email,
	message: 'Formato email non valido',
})
extend('min', {
	...min,
	message: 'Numero minimo caratteri non valido',
})
extend('max', {
	...max,
	message: 'Raggiunto numero massimo di caratteri',
})
extend('numeric', {
	...numeric,
	message: 'Numero non valido',
})
extend('confirmed', {
	...confirmed,
	message: 'Il campo non corrisponde',
})
extend('regex', {
	...regex,
	message: 'Formato non valido',
})

// Define axios
const axios = Axios.create({
	baseURL: process.env.SERVICES_URL,
	headers: {
		'X-Localization': process.env.I18N_LOCALE,
	},
})
window.axios = axios

/* eslint-disable no-new */
new Vue({
	el: '#app',
	router: router,
	store: store,
	components: {
		App,
	},
	i18n,
	template: '<App/>',
})



// WEBPACK FOOTER //
// ./src/main.js